import { Slot } from '@radix-ui/react-slot';
import { cva, type VariantProps } from 'class-variance-authority';
import { forwardRef, type ButtonHTMLAttributes } from 'react';

import { cn } from '../lib/cn.js';

/*
 * Button variants mirror the prototype's `.ba` (primary), `.bg` (ghost), and
 * `.bsm` (small) styles. Anything visually new should be added as a new
 * variant here, not as ad-hoc Tailwind classes in feature code.
 */
const buttonVariants = cva(
  [
    'inline-flex items-center justify-center gap-2 whitespace-nowrap',
    'font-mono font-medium uppercase tracking-wide',
    'rounded-sm leading-none',
    'transition-all duration-150',
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-background',
    'disabled:pointer-events-none disabled:opacity-50',
  ].join(' '),
  {
    variants: {
      variant: {
        primary:
          'bg-accent text-on-accent font-bold hover:opacity-90 hover:shadow-[0_0_10px_rgba(168,224,99,0.3)]',
        ghost:
          'bg-transparent text-text-muted border border-border-strong hover:bg-surface-2 hover:text-text',
        danger:
          'bg-danger-bg text-danger-text border border-danger/30 hover:bg-danger/20',
        discord:
          'font-discord bg-[#5865F2] text-white normal-case tracking-normal hover:bg-[#4752C4]',
      },
      size: {
        md: 'h-8 px-3.5 text-xs',
        sm: 'h-7 px-3 text-xs',
        lg: 'h-10 px-5 text-sm',
      },
    },
    // Discord buttons always render at the larger 40px / 8px-radius proportions
    // regardless of the size prop, so they match Discord's own primary buttons.
    // Compound variants are emitted after single variants, so they win twMerge.
    compoundVariants: [
      {
        variant: 'discord',
        class: 'h-10 rounded-lg px-5 text-sm',
      },
    ],
    defaultVariants: {
      variant: 'primary',
      size: 'md',
    },
  },
);

export interface ButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  asChild?: boolean;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return (
      <Comp
        ref={ref}
        className={cn(buttonVariants({ variant, size }), className)}
        {...props}
      />
    );
  },
);
Button.displayName = 'Button';

export { buttonVariants };
